import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'success'
  | 'subtle';

export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
}

// `![&]:text-white` forces the color through any cascade conflict and uses an
// explicit Tailwind layer override; combined with `font-bold` this gives the
// label crisp contrast against the deep navy fill at every render path.
const variantClasses: Record<ButtonVariant, string> = {
  primary:
    '!text-white font-bold bg-navy hover:bg-navy-deep active:bg-navy-deep disabled:bg-navy/40',
  secondary:
    'bg-white text-navy border border-navy hover:bg-canvas active:bg-canvas-hover disabled:opacity-40',
  ghost:
    'bg-transparent text-navy hover:bg-canvas active:bg-canvas-hover disabled:opacity-40',
  danger:
    '!text-white font-bold bg-status-error hover:brightness-95 active:brightness-90 disabled:opacity-40',
  success:
    '!text-white font-bold bg-status-success hover:brightness-95 active:brightness-90 disabled:opacity-40',
  subtle:
    'bg-canvas text-ink hover:bg-canvas-hover active:brightness-95 border border-line disabled:opacity-40',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-caption rounded-md gap-1.5',
  md: 'h-10 px-4 text-body-strong rounded-lg gap-2',
  lg: 'h-12 px-5 text-body-strong rounded-lg gap-2',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      iconLeft,
      iconRight,
      children,
      type = 'button',
      ...rest
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={cn(
          'inline-flex items-center justify-center font-semibold transition-colors',
          'select-none disabled:cursor-not-allowed',
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        {...rest}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            {iconLeft}
            {children}
            {iconRight}
          </>
        )}
      </button>
    );
  },
);
Button.displayName = 'Button';
